import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useState } from "react";
import { useLocation, useOutlet } from "react-router";
import { Mark, SmoothScroll, scrollToTop, useLenis } from "@mashriqain/ui";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Cursor } from "./Cursor";
import { Intro } from "./Intro";
import { ScrollProgress } from "./ScrollProgress";
import styles from "./RootLayout.module.css";

export function RootLayout() {
  return (
    <SmoothScroll>
      <Header />
      <AnimatedOutlet />
      <Footer />
      <Cursor />
      <ScrollProgress />
      <Intro />
    </SmoothScroll>
  );
}

/** Keeps the outgoing page rendered while it animates out. */
function FrozenOutlet() {
  const outlet = useOutlet();
  const [frozen] = useState(outlet);
  return frozen;
}

const CURTAIN_EASE = [0.76, 0, 0.24, 1] as const;

function AnimatedOutlet() {
  const { pathname } = useLocation();
  const lenis = useLenis();
  const reduce = useReducedMotion();

  // A curtain of ink rises over the old page, then lifts off the new one.
  // The jump to the top happens behind it, never while a page is visible.
  return (
    <AnimatePresence mode="wait" initial={false} onExitComplete={() => scrollToTop(lenis)}>
      <motion.main
        id="main"
        key={pathname}
        initial={reduce ? { opacity: 0 } : false}
        animate={{ opacity: 1 }}
        exit={reduce ? { opacity: 0 } : { opacity: 1, transition: { duration: 0.75 } }}
        transition={{ duration: 0.4 }}
      >
        {!reduce && (
          <motion.div
            className={styles.curtain}
            aria-hidden="true"
            initial={{ y: "0%" }}
            animate={{ y: "-100%", transition: { duration: 0.9, delay: 0.1, ease: CURTAIN_EASE } }}
            exit={{ y: ["100%", "0%"], transition: { duration: 0.7, ease: CURTAIN_EASE } }}
          >
            <Mark size={56} className={styles.curtainMark} />
          </motion.div>
        )}
        <FrozenOutlet />
      </motion.main>
    </AnimatePresence>
  );
}
